import React from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import StackedTopEntityChart from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useAppSelector, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useStacklineTheme } from '@stackline/ui';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import LegendWithMultipleElement from 'src/components/BeaconV2Components/Legend/LegendWithMultipleElement';
import { useMetricDataByWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useMetricDataByWeekId';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { REVIEW_SENTIMENT_ENUM } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/constants';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { saveWeeklyStackedBarChartAsDelimitedData } from 'src/utils/chartUtils';
import AdvancedSearchParser from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/AdvancedSearchParser';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

export interface StackedChartDataSet {
  name: string;
  data: { y: number; color: string }[];
  color: string;
}

interface ReviewVolumeBySentiment {
  fieldName: 'weekId';
  fieldId: string; // '202405';
  weekId: number;
  value: number;
  count: number;
  additionalValues: {
    reviewSentiment_value_count_value: number;
    reviewSentiment_value_count_count: number;
  };
  additionalMetaData: {
    weekId: string;
    reviewSentiment: '1' | '0' | '-1';
    name: string;
  };
}

export const useWeeklyReviewVolumeBySentiment = () => {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const { data: sentimentValuesByWeek, isLoading: childWeeklyRatingVolumeBySentimentLoading } = useMetricDataByWeekId({
    startWeekId: startWeek,
    endWeekId: endWeek,
    requestId: 'stackedColumn-weeklyReviewVolumeBySentiment',
    fields: [{ name: 'reviewSentiment' }],
    indexName: BEACON_INDEX_NAMES.REVIEWS_METRICS,
    requestBuilder(builder) {
      builder.modifyAggregation((aggregation) => {
        aggregation.setGroupByFieldName('weekId,reviewSentiment');
        return aggregation;
      });
      return builder;
    },
    responseParser(response) {
      const parser = new AdvancedSearchParser(response);
      const weeklySentimentValues = parser.getAggregations()?.by_weekId?.reduce((accumulator, current) => {
        const weekId = current.fieldId;
        const sentimentEnumType = Number(current.additionalMetaData?.reviewSentiment);
        const value = current?.value ?? 0;

        if (!accumulator[weekId]) {
          accumulator[weekId] = {};
        }

        accumulator[weekId][sentimentEnumType] = value;
        return accumulator;
      }, {});

      return weeklySentimentValues;
    }
  });

  return {
    data: sentimentValuesByWeek,
    isLoading: childWeeklyRatingVolumeBySentimentLoading
  };
};
/**
 * Weekly Review volume by sentiment - can be found on Sentiment tab - the stacked column chart
 */
const SentimentWeeklyReviewVolumeStackedChart = () => {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const theme = useStacklineTheme();

  const { data, isLoading: weeklyReviewVolumeByStarLoading } = useWeeklyReviewVolumeBySentiment();

  const categoryData: string[] = [];

  const stackedChartDataset: StackedChartDataSet[] = [
    { name: 'Negative', data: [], color: theme.colors.accentMagenta },
    { name: 'Neutral', data: [], color: theme.colors.accentGold },
    { name: 'Positive', data: [], color: theme.colors.accentPine }
  ];

  Object.entries(data).forEach(([weekId, sentimentBucket]) => {
    if (weekId !== undefined) {
      categoryData.push(String(weekId));

      const dataItems = [
        sentimentBucket[REVIEW_SENTIMENT_ENUM.NEGATIVE] || 0,
        sentimentBucket[REVIEW_SENTIMENT_ENUM.NEUTRAL] || 0,
        sentimentBucket[REVIEW_SENTIMENT_ENUM.POSITIVE] || 0
      ];

      dataItems.forEach((value, dataItemIndex) => {
        stackedChartDataset[dataItemIndex].data.push({
          y: Math.round(value),
          color: stackedChartDataset[dataItemIndex].color
        });
      });
    }
  });

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        return saveWeeklyStackedBarChartAsDelimitedData({ chartData: stackedChartDataset, categoryData });
      }}
      loading={weeklyReviewVolumeByStarLoading}
      loadingComponent={<SplineChartLoading width={900} />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the review volume grouped by sentiment for ${entityPhrase} `}
            {taxonomyText}{' '}
            {`from reviews written during the
            ${mainTimePeriodShortDisplayName} period.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      customLegends={
        <LegendWithMultipleElement
          multipleLegendsProps={[
            { displayName: 'Positive', barColor: 'accentPine' },
            { displayName: 'Neutral', barColor: 'accentGold' },
            { displayName: 'Negative', barColor: 'accentMagenta' }
          ]}
        />
      }
      title="Review Volume by Sentiment"
    >
      <StackedTopEntityChart
        maxNumberOfColumns={8}
        reverseAxisStartPosition
        categories={categoryData}
        chartSeries={[
          {
            type: 'column',
            data: stackedChartDataset,
            metricType: METRICTYPE.VOLUME,
            groupBy: 'weekId'
          }
        ]}
        tooltipCategoryTitle="REVIEW VOLUME"
        chartOptionsOverride={(option) => {
          const copiedOption = { ...option };
          copiedOption.chart.marginLeft = 40;
          copiedOption.chart.width = 1012;
          copiedOption.yAxis.labels = {
            style: {
              color: 'transparent'
            }
          };

          return option;
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default SentimentWeeklyReviewVolumeStackedChart;
